import {
  Component,
  Input,
  ChangeDetectorRef,
  ElementRef,
  Output,
  EventEmitter,
  inject,
  ViewChild,
  viewChild,
  signal,
  effect,
} from '@angular/core';
import { find, first, maxBy, without } from 'lodash';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { IHotspot } from '../../hotspot';
import { PrimeNGConfig } from '@gridscale/primeng17/api';
import { ZIndexUtils } from '@gridscale/primeng17/utils';
import { A11yFocusDirective } from '../../../helper/directives/a11yfocus.directive';

interface BadgeMessage {
  type: string;
  key?: string;
  badness: number;
}

@Component({
    selector: 'ig-layout-header-flyout-menu',
    templateUrl: './layout-header-flyout-menu.component.html',
    styleUrls: ['./layout-header-flyout-menu.component.scss'],
    host: {
        '(document:click)': 'onClickSomewhere($event)',
    },
    standalone: false
})
export class LayoutHeaderFlyoutMenuComponent {
  @Input() halfWidth = false;
  @Input() icon?: string;
  @Input() visible = false;
  @Input() noTopPadding = false;
  @Input() closeOnRouteChange = false;
  @Input() hotspot?: IHotspot;
  @Input() ariaLabel = '';
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @ViewChild('overlay') overlay?: ElementRef<HTMLElement>;

  readonly #cdRef = inject(ChangeDetectorRef);

  showBadge = false;
  badgeMessages: BadgeMessage[] = [];
  badgeWasVisible = false;
  showBadgeAsDot = false;
  badnessColors: ('green' | 'red' | 'orange')[] = ['green', 'orange', 'red'];
  badgeColor: 'green' | 'red' | 'orange' = 'green';

  isOpen = signal(false);
  #isOpenLast = false;
  pauseEvents: boolean = false;
  openCloseTimeout: number | null = null;

  overlayFocus = viewChild<A11yFocusDirective>('overlayFocus');


  constructor(private _eref: ElementRef, private readonly router: Router, private readonly config: PrimeNGConfig) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.closeOnRouteChange) {
          this.close();
        }
      });

    effect(() => {
      const overlayFocus = this.overlayFocus();
      const isOpen = this.isOpen();

      if (overlayFocus && this.#isOpenLast !== isOpen) {
        this.#isOpenLast = isOpen;
        if (isOpen) {
          overlayFocus.triggerShow();
        } else {
          overlayFocus.triggerHide();
        }
      }

    })
  }

  showIcon() {
    this.visible = true;
  }
  hideIcon() {
    this.visible = false;
  }

  click() {
    this.pauseEvents = true; // pause enter/leave event listening
    window.setTimeout(() => (this.pauseEvents = false), 100);

    if (this.openCloseTimeout) {
      window.clearTimeout(this.openCloseTimeout);
      this.openCloseTimeout = null;
    }

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onClickSomewhere(event: Event) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  enter() {
    if (this.pauseEvents) return;

    if (this.openCloseTimeout) {
      window.clearTimeout(this.openCloseTimeout);
    }
    this.openCloseTimeout = window.setTimeout(() => this.open(), 100);
  }
  leave() {
    if (this.pauseEvents) return;

    if (this.openCloseTimeout) {
      window.clearTimeout(this.openCloseTimeout);
    }
    this.openCloseTimeout = window.setTimeout(() => this.close(), 100);
  }

  open() {
    if (!this.isOpen()) {
      this.onOpen.emit();
    }
    this.isOpen.set(true);

    window.setTimeout(() => {
      ZIndexUtils.set('ig-layout-header-flyout-menu', this.overlay?.nativeElement, this.config.zIndex.overlay);
    });

    this.#cdRef.markForCheck();
  }
  close() {
    if (this.isOpen()) {
      this.onClose.emit();
    }
    this.isOpen.set(false);

    this.#cdRef.markForCheck();
  }

  /**
   * add a badge message resulting in a badge with appropriate number to be displayed
   * @param msgType : string - messageType, used for grouping
   * @param badness : number - determines the badge color from green (badness = 0) to red (badness = 2)
   * @param msgKey : string - optional message key to mark similar message from one types as different message
   */
  addBadgeMessage(msgType: string, badness: number = 0, msgKey?: string) {
    if (badness > this.badnessColors.length - 1) {
      badness = this.badnessColors.length - 1;
    }

    if (
      undefined === find(this.badgeMessages, { type: msgType, key: msgKey })
    ) {
      this.badgeMessages.push({
        type: msgType,
        key: msgKey,
        badness: badness,
      });
      this.badgeWasVisible = true;
      this.showBadgeAsDot =
        this.badgeMessages.length === 1 &&
        (msgType === 'incident' || msgType === 'status');
    }

    this.badgeColor =
      this.badnessColors[maxBy(this.badgeMessages as any[], 'badness').badness];

    if (!this.showBadge) {
      // we show the badge after a second to make the animation better visible
      window.setTimeout(() => {
        this.showBadge = true;
      }, 1000);
    }

    this.#cdRef.detectChanges();
  }

  /**
   * remove a badge message resulting in number in badge beeing decreased or badge is hidden when no badge messages present
   */
  removeBadgeMessage(msgType: string, msgKey?: string) {
    const findKey: { type: string; key?: string } = { type: msgType };
    if (msgKey) {
      findKey.key = msgKey;
    }
    let removed = false;

    let msg = find(this.badgeMessages, findKey);
    while (msg) {
      removed = true;
      this.badgeMessages = <BadgeMessage[]>without(this.badgeMessages, msg);
      this.showBadgeAsDot =
        this.badgeMessages.length === 1 &&
        (first(this.badgeMessages)!.type === 'incident' ||
          first(this.badgeMessages)!.type === 'status');

      // if we removed a bad message, check if there are still bad ones, otherwise set color to green
      if (this.badgeMessages.length) {
        this.badgeColor =
          this.badnessColors[
            maxBy(this.badgeMessages as any[], 'badness').badness
          ];
      } else {
        this.badgeColor = this.badnessColors[0];
      }

      msg = find(this.badgeMessages, findKey);
    }

    if (removed && this.showBadge && this.badgeMessages.length < 1) {
      // we shortly remove and then reinsert the element to let css replay the animation
      this.showBadge = false;
      window.setTimeout(() => {
        this.showBadge = true;
      }, 1);
    }

    this.#cdRef.detectChanges();
  }
}
